// Firebase Analytics Utilities
import { logEvent, setUserProperties as setFirebaseUserProperties } from 'firebase/analytics';
import { analytics } from '../config/firebase';

/**
 * Track user registration
 * @param {string} userType - Type of user (customer, vendor, admin)
 * @param {string} method - Registration method (email, google, etc)
 */
export const trackUserRegistration = (userType, method = 'email') => {
  logEvent(analytics, 'user_registration', {
    user_type: userType,
    method: method,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track user login
 * @param {string} method - Login method
 */
export const trackUserLogin = (method = 'email') => {
  logEvent(analytics, 'user_login', {
    method: method,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track product view
 * @param {string} productId - Product ID
 * @param {Object} productData - Product information
 */
export const trackProductView = (productId, productData = {}) => {
  logEvent(analytics, 'view_item', {
    items: [
      {
        item_id: productId,
        item_name: productData.name || 'Unknown',
        item_category: productData.category || 'Unknown',
        price: productData.price || 0,
        currency: 'ZAR'
      }
    ]
  });
};

/**
 * Track product search
 * @param {string} searchQuery - Search query
 * @param {number} resultsCount - Number of results
 */
export const trackProductSearch = (searchQuery, resultsCount = 0) => {
  logEvent(analytics, 'search', {
    search_term: searchQuery,
    results_count: resultsCount,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track add to cart
 * @param {string} productId - Product ID
 * @param {number} quantity - Quantity added
 * @param {number} price - Product price
 */
export const trackAddToCart = (productId, quantity = 1, price = 0) => {
  logEvent(analytics, 'add_to_cart', {
    items: [
      {
        item_id: productId,
        quantity: quantity,
        price: price,
        currency: 'ZAR'
      }
    ]
  });
};

/**
 * Track checkout initiation
 * @param {Array} cartItems - Items in cart
 * @param {number} totalValue - Total cart value
 */
export const trackCheckoutStart = (cartItems = [], totalValue = 0) => {
  logEvent(analytics, 'begin_checkout', {
    items: cartItems.map(item => ({
      item_id: item._id || item.id,
      item_name: item.name,
      quantity: item.quantity,
      price: item.price,
      currency: 'ZAR'
    })),
    value: totalValue,
    currency: 'ZAR'
  });
};

/**
 * Track purchase completion
 * @param {string} orderId - Order ID
 * @param {Array} items - Purchased items
 * @param {number} totalValue - Total purchase value
 * @param {string} paymentMethod - Payment method used
 */
export const trackPurchase = (orderId, items = [], totalValue = 0, paymentMethod = 'unknown') => {
  logEvent(analytics, 'purchase', {
    transaction_id: orderId,
    value: totalValue,
    currency: 'ZAR',
    payment_type: paymentMethod,
    items: items.map(item => ({
      item_id: item._id || item.id,
      item_name: item.name,
      quantity: item.quantity,
      price: item.price,
      currency: 'ZAR'
    }))
  });
};

/**
 * Track vendor application submission
 * @param {string} businessName - Vendor business name
 * @param {number} productCount - Number of products submitted
 */
export const trackVendorApplication = (businessName, productCount = 0) => {
  logEvent(analytics, 'vendor_application', {
    business_name: businessName,
    product_count: productCount,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track page view
 * @param {string} pageName - Page name/route
 * @param {Object} metadata - Additional metadata
 */
export const trackPageView = (pageName, metadata = {}) => {
  logEvent(analytics, 'page_view', {
    page_name: pageName,
    timestamp: new Date().toISOString(),
    ...metadata
  });
};

/**
 * Track error
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {string} severity - Error severity (low, medium, high)
 */
export const trackError = (errorType, errorMessage, severity = 'medium') => {
  logEvent(analytics, 'error', {
    error_type: errorType,
    error_message: errorMessage,
    severity: severity,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track feature usage
 * @param {string} featureName - Feature being used
 * @param {Object} data - Feature-specific data
 */
export const trackFeatureUsage = (featureName, data = {}) => {
  logEvent(analytics, 'feature_usage', {
    feature_name: featureName,
    timestamp: new Date().toISOString(),
    ...data
  });
};

/**
 * Set user properties for segmentation
 * @param {string} userId - User ID
 * @param {Object} properties - User properties
 */
export const setUserProperties = (userId, properties = {}) => {
  setFirebaseUserProperties(analytics, {
    user_id: userId,
    ...properties
  });
};

/**
 * Track image upload stats
 * @param {number} imageCount - Number of images uploaded
 * @param {string} category - Image category (product, vendor, etc)
 */
export const trackImageUpload = (imageCount = 1, category = 'general') => {
  logEvent(analytics, 'image_upload_stats', {
    image_count: imageCount,
    category: category,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track page performance
 * @param {string} pageName - Page name
 * @param {number} loadTime - Page load time in ms
 */
export const trackPagePerformance = (pageName, loadTime) => {
  logEvent(analytics, 'page_performance', {
    page_name: pageName,
    load_time_ms: loadTime,
    timestamp: new Date().toISOString()
  });
};

export default {
  trackUserRegistration,
  trackUserLogin,
  trackProductView,
  trackProductSearch,
  trackAddToCart,
  trackCheckoutStart,
  trackPurchase,
  trackVendorApplication,
  trackPageView,
  trackError,
  trackFeatureUsage,
  setUserProperties,
  trackImageUpload,
  trackPagePerformance
};
